import { useFormikContext } from "formik";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";

import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import { readAndValidateImage } from "@/components/screens/ProductFormPage/helpers";
import useToast from "@/contexts/toast-context";
import { isFileSizeValid, isValidFileType } from "@/helpers/file-helpers";
import useDialog from "@/hooks/use-dialog";
import { ICampaignImage } from "@/interfaces/campaign.interface";
import { CampaignFormMessages } from "@/messages/campaigns/campaign-form.messages";

type UseCampaignImageProps = {
  messages: CampaignFormMessages;
};

function useCampaignImage({ messages }: UseCampaignImageProps) {
  const { values, setFieldValue } = useFormikContext<CampaignFormData>();
  const { showToast } = useToast();
  const { isVisible, handleOpen, handleClose } = useDialog();
  const [imageToDelete, setImageToDelete] = useState<ICampaignImage | null>(null);

  const campaignImages = useMemo(() => values.campaignImages || [], [values.campaignImages]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      if (!isValidFileType(file.name)) {
        showToast({ text: messages.unsupportedFileFormat, type: "error" });
        return;
      }

      if (!isFileSizeValid(file.size)) {
        showToast({ text: messages.fileTooLarge, type: "error" });
        return;
      }

      readAndValidateImage(
        messages,
        file,
        (imgPreview) => {
          const { preview } = imgPreview;

          let previewUrl = "";
          if (typeof preview === "string") {
            previewUrl = preview;
          } else if (preview instanceof ArrayBuffer) {
            const base64String = arrayBufferToBase64(preview);
            previewUrl = `data:image/jpeg;base64,${base64String}`;
          }

          const newImage: ICampaignImage = {
            file,
            url: previewUrl,
            preview: previewUrl,
          };

          // Se já existe uma imagem, adiciona o ID dela ao campo imagesToDelete
          if (campaignImages.length > 0) {
            const currentImage = campaignImages[0];
            if (currentImage.id !== undefined) {
              const updatedImagesToDelete = [...(values.imagesToDelete || []), currentImage.id];
              setFieldValue("imagesToDelete", updatedImagesToDelete);
            }
          }

          setFieldValue("campaignImages", [newImage]);
        },
        (errorMessage) => showToast({ text: errorMessage, type: "error" })
      );
    },
    [campaignImages, messages, setFieldValue, showToast, values.imagesToDelete]
  );

  const handleConfirmDelete = useCallback(() => {
    if (imageToDelete) {
      const updatedImages = campaignImages.filter((img) => img !== imageToDelete);
      setFieldValue("campaignImages", updatedImages);

      if (imageToDelete.id !== undefined) {
        const updatedImagesToDelete = [...(values.imagesToDelete || []), imageToDelete.id];
        setFieldValue("imagesToDelete", updatedImagesToDelete);
      }

      setImageToDelete(null);
    }

    handleClose();
  }, [campaignImages, handleClose, imageToDelete, setFieldValue, values.imagesToDelete]);

  const promptRemoveImage = useCallback(
    (event: React.MouseEvent, image: ICampaignImage) => {
      event.stopPropagation();
      setImageToDelete(image);
      handleOpen();
    },
    [handleOpen]
  );

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return {
    campaignImages,
    getRootProps,
    getInputProps,
    promptRemoveImage,
    isVisible,
    handleClose,
    handleConfirmDelete,
    imageToDelete,
  };
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export default useCampaignImage;
